/**
 * MangaCreateWizard — guided 3-step creation flow for a new manga/comic project.
 * Ensures the AI has every piece of information it needs from the very start:
 * title, premise, genre, art style, page size and reading direction.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';
import { BookOpen, LayoutTemplate, Palette, ChevronLeft, ChevronRight, Loader2, Check } from 'lucide-react';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
  ResponsiveModalFooter,
} from './ui/responsive-modal';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { StylePicker, PROJECT_STYLE_PRESETS } from './style-picker';
import { cn } from '../lib/utils';
import { api } from '../lib/api';
import { useAppStore } from '../stores/app-store';
import { notify } from '../lib/toast';

// ─── Types ────────────────────────────────────────────────────────────────────

interface MangaCreateWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (projectId: string) => void;
}

type WizardStep = 'basic' | 'format' | 'style';

interface PageSizePreset {
  id: string;
  width: number;
  height: number;
  labelKey: string;
  defaultLabel: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE_PRESETS: PageSizePreset[] = [
  { id: 'manga', width: 1000, height: 1414, labelKey: 'presetManga', defaultLabel: 'Mangá B5 (padrão)' },
  { id: 'comic', width: 1000, height: 1545, labelKey: 'presetComic', defaultLabel: 'HQ americana' },
  { id: 'square', width: 1000, height: 1000, labelKey: 'presetSquare', defaultLabel: 'Quadrado' },
  { id: 'webtoon', width: 800, height: 2000, labelKey: 'presetWebtoon', defaultLabel: 'Webtoon (rolagem vertical)' },
  { id: 'landscape', width: 1414, height: 1000, labelKey: 'presetLandscape', defaultLabel: 'Paisagem' },
];

const MIN_DIM = 400;
const MAX_DIM = 4000;
const DEFAULT_STYLE =
  PROJECT_STYLE_PRESETS.find((p) => p.id === 'anime')?.clause ?? PROJECT_STYLE_PRESETS[0].clause;

const STEPS: { id: WizardStep; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'basic', icon: BookOpen },
  { id: 'format', icon: LayoutTemplate },
  { id: 'style', icon: Palette },
];

const STEP_LABEL_KEYS: Record<WizardStep, string> = {
  basic: 'stepBasic',
  format: 'stepFormat',
  style: 'stepStyle',
};

function getStepIndex(step: WizardStep): number {
  return STEPS.findIndex((s) => s.id === step);
}

function clamp(n: number, min: number, max: number): number {
  if (Number.isNaN(n)) return min;
  return Math.min(max, Math.max(min, n));
}

// ─── Stepper ──────────────────────────────────────────────────────────────────

function Stepper({ currentStep }: { currentStep: WizardStep }) {
  const { t } = useTranslation();
  const currentIndex = getStepIndex(currentStep);

  return (
    <div className="flex items-center gap-0 mb-5">
      {STEPS.map((step, i) => {
        const isDone = i < currentIndex;
        const isActive = step.id === currentStep;
        const Icon = step.icon;

        return (
          <div key={step.id} className="flex items-center flex-1">
            <div className="flex flex-col items-center gap-1.5 relative">
              <div
                className={cn(
                  'w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300',
                  isDone
                    ? 'bg-primary text-primary-foreground'
                    : isActive
                      ? 'bg-primary/10 border-2 border-primary text-primary'
                      : 'bg-muted border border-border text-muted-foreground',
                )}
              >
                {isDone ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
              </div>
              <span
                className={cn(
                  'text-[9px] font-medium uppercase tracking-wider whitespace-nowrap',
                  isActive ? 'text-primary' : isDone ? 'text-primary/70' : 'text-muted-foreground',
                )}
              >
                {t(`mangaWizard.${STEP_LABEL_KEYS[step.id]}`, step.id)}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                className={cn(
                  'flex-1 h-px mx-2 mb-4 transition-colors duration-300',
                  i < currentIndex ? 'bg-primary' : 'bg-border',
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Size preset card ─────────────────────────────────────────────────────────

interface SizePresetCardProps {
  active: boolean;
  onClick: () => void;
  label: string;
  width: number;
  height: number;
}

function SizePresetCard({ active, onClick, label, width, height }: SizePresetCardProps) {
  const ratio = width / height;
  const maxBox = 34;
  const boxW = ratio >= 1 ? maxBox : Math.max(10, Math.round(maxBox * ratio));
  const boxH = ratio >= 1 ? Math.max(10, Math.round(maxBox / ratio)) : maxBox;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex flex-col items-center gap-2 rounded-xl border p-3 transition-colors',
        active
          ? 'border-primary bg-primary/5 shadow-sm shadow-primary/10'
          : 'border-border bg-card hover:border-primary/30 hover:bg-muted/40',
      )}
    >
      <div className="flex h-11 w-11 items-center justify-center flex-shrink-0">
        <div
          className={cn('rounded-[3px] border-2', active ? 'border-primary' : 'border-muted-foreground/50')}
          style={{ width: boxW, height: boxH }}
        />
      </div>
      <span className="text-xs font-medium text-foreground text-center leading-tight">{label}</span>
      <span className="font-mono text-[10px] text-muted-foreground">
        {width}×{height}
      </span>
    </button>
  );
}

// ─── Step 1: Básico ─────────────────────────────────────────────────────────

interface StepBasicProps {
  title: string;
  description: string;
  genre: string;
  onChangeTitle: (v: string) => void;
  onChangeDescription: (v: string) => void;
  onChangeGenre: (v: string) => void;
}

function StepBasic({ title, description, genre, onChangeTitle, onChangeDescription, onChangeGenre }: StepBasicProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="manga-title">{t('mangaWizard.titleLabel', 'Título')}</Label>
        <Input
          id="manga-title"
          value={title}
          onChange={(e) => onChangeTitle(e.target.value)}
          placeholder={t('mangaWizard.titlePlaceholder', 'Ex.: A Lâmina de Vidro')}
          className="h-11"
          autoFocus
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="manga-description">{t('mangaWizard.premiseLabel', 'Premissa / descrição')}</Label>
        <Textarea
          id="manga-description"
          value={description}
          onChange={(e) => onChangeDescription(e.target.value)}
          placeholder={t(
            'mangaWizard.premisePlaceholder',
            'Descreva o mundo, os personagens principais e o conflito central...',
          )}
          className="min-h-32 resize-y"
        />
        <p className="text-xs text-muted-foreground">
          {t('mangaWizard.premiseHint', 'A IA usa essa descrição para gerar arte, roteiro e personagens coerentes.')}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="manga-genre">{t('mangaWizard.genreLabel', 'Gênero')}</Label>
        <Input
          id="manga-genre"
          value={genre}
          onChange={(e) => onChangeGenre(e.target.value)}
          placeholder={t('mangaWizard.genrePlaceholder', 'Ex.: Fantasia sombria, Shonen de ação...')}
          className="h-11"
        />
      </div>
    </div>
  );
}

// ─── Reading-direction mini-diagram ──────────────────────────────────────────

/** A 2×2 mini-grid whose panels are numbered in the given reading direction —
 * shows at a glance whether panels flow right→left (manga) or left→right (HQ). */
function DirectionDiagram({ rtl, active }: { rtl: boolean; active: boolean }) {
  // Cells laid out TL, TR, BL, BR; the number each shows is its reading rank.
  const order = rtl ? [2, 1, 4, 3] : [1, 2, 3, 4];
  return (
    <span
      aria-hidden
      className={cn(
        'grid h-12 w-12 shrink-0 grid-cols-2 gap-1 rounded-lg border p-1',
        active ? 'border-primary/60 bg-primary/5' : 'border-border bg-muted/40',
      )}>
      {order.map((n, i) => (
        <span
          key={i}
          className={cn(
            'flex items-center justify-center rounded-[3px] text-[9px] font-bold leading-none',
            active ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground',
          )}>
          {n}
        </span>
      ))}
    </span>
  );
}

// ─── Step 2: Formato ────────────────────────────────────────────────────────

interface StepFormatProps {
  sizePresetId: string;
  customWidth: number;
  customHeight: number;
  readingDirection: 'rtl' | 'ltr';
  onSelectPreset: (id: string) => void;
  onChangeCustomWidth: (n: number) => void;
  onChangeCustomHeight: (n: number) => void;
  onChangeReadingDirection: (dir: 'rtl' | 'ltr') => void;
}

function StepFormat({
  sizePresetId,
  customWidth,
  customHeight,
  readingDirection,
  onSelectPreset,
  onChangeCustomWidth,
  onChangeCustomHeight,
  onChangeReadingDirection,
}: StepFormatProps) {
  const { t } = useTranslation();
  const isCustom = sizePresetId === 'custom';

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <Label>{t('mangaWizard.pageSizeLabel', 'Tamanho de página')}</Label>
        <div className="grid grid-cols-3 sm:grid-cols-3 gap-2.5">
          {PAGE_SIZE_PRESETS.map((preset) => (
            <SizePresetCard
              key={preset.id}
              active={sizePresetId === preset.id}
              onClick={() => onSelectPreset(preset.id)}
              label={t(`mangaWizard.${preset.labelKey}`, preset.defaultLabel)}
              width={preset.width}
              height={preset.height}
            />
          ))}
          <SizePresetCard
            active={isCustom}
            onClick={() => onSelectPreset('custom')}
            label={t('mangaWizard.presetCustom', 'Personalizado')}
            width={customWidth}
            height={customHeight}
          />
        </div>

        {isCustom && (
          <div className="grid grid-cols-2 gap-3 pt-1">
            <div className="space-y-1.5">
              <Label htmlFor="manga-custom-width" className="text-xs font-normal text-muted-foreground">
                {t('mangaWizard.customWidthLabel', 'Largura (px)')}
              </Label>
              <Input
                id="manga-custom-width"
                type="number"
                min={MIN_DIM}
                max={MAX_DIM}
                value={customWidth}
                onChange={(e) => onChangeCustomWidth(clamp(Number(e.target.value), MIN_DIM, MAX_DIM))}
                className="h-10"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="manga-custom-height" className="text-xs font-normal text-muted-foreground">
                {t('mangaWizard.customHeightLabel', 'Altura (px)')}
              </Label>
              <Input
                id="manga-custom-height"
                type="number"
                min={MIN_DIM}
                max={MAX_DIM}
                value={customHeight}
                onChange={(e) => onChangeCustomHeight(clamp(Number(e.target.value), MIN_DIM, MAX_DIM))}
                className="h-10"
              />
            </div>
          </div>
        )}
      </div>

      <div className="space-y-3">
        <Label>{t('mangaWizard.readingDirectionLabel', 'Direção de leitura')}</Label>
        <div className="grid grid-cols-2 gap-2.5">
          <button
            type="button"
            onClick={() => onChangeReadingDirection('rtl')}
            aria-pressed={readingDirection === 'rtl'}
            className={cn(
              'flex items-start gap-3 rounded-xl border p-3.5 text-left transition-colors',
              readingDirection === 'rtl'
                ? 'border-primary bg-primary/5'
                : 'border-border bg-card hover:border-primary/30 hover:bg-muted/40',
            )}
          >
            <DirectionDiagram rtl active={readingDirection === 'rtl'} />
            <span className="min-w-0">
              <span className="block text-sm font-medium text-foreground">{t('mangaWizard.rtlTitle', 'Mangá')}</span>
              <span className="mt-0.5 block text-xs text-muted-foreground">
                {t('mangaWizard.rtlHint', 'Leitura direita → esquerda (padrão japonês).')}
              </span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => onChangeReadingDirection('ltr')}
            aria-pressed={readingDirection === 'ltr'}
            className={cn(
              'flex items-start gap-3 rounded-xl border p-3.5 text-left transition-colors',
              readingDirection === 'ltr'
                ? 'border-primary bg-primary/5'
                : 'border-border bg-card hover:border-primary/30 hover:bg-muted/40',
            )}
          >
            <DirectionDiagram rtl={false} active={readingDirection === 'ltr'} />
            <span className="min-w-0">
              <span className="block text-sm font-medium text-foreground">{t('mangaWizard.ltrTitle', 'HQ / Comic')}</span>
              <span className="mt-0.5 block text-xs text-muted-foreground">
                {t('mangaWizard.ltrHint', 'Leitura esquerda → direita (padrão ocidental).')}
              </span>
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Step 3: Estilo ─────────────────────────────────────────────────────────

interface StepStyleProps {
  mangaStyle: string;
  onChangeMangaStyle: (v: string) => void;
}

function StepStyle({ mangaStyle, onChangeMangaStyle }: StepStyleProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <Label>{t('mangaWizard.artStyleLabel', 'Estilo de arte')}</Label>
      <StylePicker value={mangaStyle} onChange={onChangeMangaStyle} />
      <p className="text-xs text-muted-foreground">
        {t('mangaWizard.artStyleHint', 'Aplicado a todas as artes geradas neste mangá.')}
      </p>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function MangaCreateWizard({ open, onOpenChange, onCreated }: MangaCreateWizardProps) {
  const { t } = useTranslation();
  const fetchProjects = useAppStore((s) => s.fetchProjects);

  const [step, setStep] = useState<WizardStep>('basic');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [genre, setGenre] = useState('');
  const [sizePresetId, setSizePresetId] = useState('manga');
  const [customWidth, setCustomWidth] = useState(1000);
  const [customHeight, setCustomHeight] = useState(1414);
  const [readingDirection, setReadingDirection] = useState<'rtl' | 'ltr'>('rtl');
  const [mangaStyle, setMangaStyle] = useState(DEFAULT_STYLE);
  const [isCreating, setIsCreating] = useState(false);

  const resetState = () => {
    setStep('basic');
    setTitle('');
    setDescription('');
    setGenre('');
    setSizePresetId('manga');
    setCustomWidth(1000);
    setCustomHeight(1414);
    setReadingDirection('rtl');
    setMangaStyle(DEFAULT_STYLE);
    setIsCreating(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && !isCreating) resetState();
    onOpenChange(isOpen);
  };

  const activePreset = PAGE_SIZE_PRESETS.find((p) => p.id === sizePresetId);
  const width = activePreset ? activePreset.width : clamp(customWidth, MIN_DIM, MAX_DIM);
  const height = activePreset ? activePreset.height : clamp(customHeight, MIN_DIM, MAX_DIM);

  const canProceedFromBasic = title.trim().length > 0;
  const canCreate = mangaStyle.trim().length > 0;

  const handleNext = () => {
    if (step === 'basic') {
      if (!canProceedFromBasic) {
        notify.error(t('mangaWizard.toastTitleRequired', 'Dê um título ao seu mangá antes de continuar.'));
        return;
      }
      setStep('format');
    } else if (step === 'format') {
      setStep('style');
    }
  };

  const handleBack = () => {
    const index = getStepIndex(step);
    if (index > 0) {
      setStep(STEPS[index - 1].id);
    } else {
      handleOpenChange(false);
    }
  };

  const handleCreate = async () => {
    if (!canProceedFromBasic) {
      setStep('basic');
      notify.error(t('mangaWizard.toastTitleRequired', 'Dê um título ao seu mangá antes de continuar.'));
      return;
    }
    if (!canCreate) {
      notify.error(t('mangaWizard.toastStyleRequired', 'Escolha um estilo de arte antes de criar.'));
      return;
    }

    setIsCreating(true);
    try {
      const { project } = await api.createProject({
        title: title.trim(),
        genre: genre.trim() || undefined,
        description: description.trim() || undefined,
        kind: 'manga',
      });
      await api.updateProject(project.id, {
        mangaStyle: mangaStyle.trim(),
        readingDirection,
      });
      await api.createMangaPage(project.id, { width, height });
      await fetchProjects();
      notify.success(t('mangaWizard.toastCreated', 'Mangá criado com sucesso!'));
      resetState();
      onOpenChange(false);
      onCreated(project.id);
    } catch {
      notify.error(t('common.error', 'Erro'), t('mangaWizard.toastCreateFailed', 'Não foi possível criar o mangá.'));
      setIsCreating(false);
    }
  };

  const nextLabel = (() => {
    if (step === 'style') return isCreating ? t('mangaWizard.creating', 'Criando...') : t('mangaWizard.createManga', 'Criar mangá');
    return t('mangaWizard.continueLabel', 'Continuar');
  })();

  return (
    <ResponsiveModal open={open} onOpenChange={handleOpenChange}>
      <ResponsiveModalContent className="sm:max-w-xl">
        <ResponsiveModalHeader>
          <ResponsiveModalTitle className="font-serif">
            {t('mangaWizard.headerTitle', 'Criar novo mangá / HQ')}
          </ResponsiveModalTitle>
          <ResponsiveModalDescription>
            {t('mangaWizard.headerDescription', 'Defina o essencial para a IA já começar alinhada com sua visão.')}
          </ResponsiveModalDescription>
        </ResponsiveModalHeader>

        <Stepper currentStep={step} />

        <div className="rounded-2xl border border-border bg-card/40 p-4 sm:p-5">
          <AnimatePresence mode="wait">
            {step === 'basic' && (
              <motion.div
                key="basic"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                <StepBasic
                  title={title}
                  description={description}
                  genre={genre}
                  onChangeTitle={setTitle}
                  onChangeDescription={setDescription}
                  onChangeGenre={setGenre}
                />
              </motion.div>
            )}

            {step === 'format' && (
              <motion.div
                key="format"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                <StepFormat
                  sizePresetId={sizePresetId}
                  customWidth={customWidth}
                  customHeight={customHeight}
                  readingDirection={readingDirection}
                  onSelectPreset={setSizePresetId}
                  onChangeCustomWidth={setCustomWidth}
                  onChangeCustomHeight={setCustomHeight}
                  onChangeReadingDirection={setReadingDirection}
                />
              </motion.div>
            )}

            {step === 'style' && (
              <motion.div
                key="style"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                <StepStyle mangaStyle={mangaStyle} onChangeMangaStyle={setMangaStyle} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <ResponsiveModalFooter className="flex-row items-center justify-between sm:justify-between">
          <Button type="button" variant="ghost" onClick={handleBack} disabled={isCreating}>
            <ChevronLeft className="h-4 w-4" />
            {step === 'basic' ? t('mangaWizard.cancel', 'Cancelar') : t('mangaWizard.back', 'Voltar')}
          </Button>

          <Button
            type="button"
            onClick={step === 'style' ? handleCreate : handleNext}
            disabled={isCreating}
            className="min-w-36 gap-2"
          >
            {isCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-4 w-4" />}
            {nextLabel}
          </Button>
        </ResponsiveModalFooter>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
